import React, { useCallback, useMemo, useState } from 'react';

import { ArrowBackIcon, ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import { Flex } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import usersApi from 'api/users';
import AppModal from 'components/modules/AppModal';
import Button from 'components/primitives/Button';
import { Caret } from 'components/primitives/SVG';
import Typography from 'components/primitives/Typography';
import { FETCH_USER_DETAILS } from 'constants/queries';

import Notes from '../Notes';
import UserFullDetails from '../UserFullDetails';
import UserInfoEdit from '../UserInfoEdit';
import { UserDetailsProps, MODE } from './UserDetails.props';

const UserDetailsView = ({ id, ...props }: UserDetailsProps): JSX.Element => {
  const history = useHistory();

  const [mode, setMode] = useState(MODE.DEFAULT);
  const [notesOpen, setNotesOpen] = useState(false);

  const { data: fullDetails, refetch } = useQuery(
    `${FETCH_USER_DETAILS}${id}`,
    () => (id ? usersApi.getDetails(id) : undefined)
  );

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const Content = useMemo(() => {
    if (!fullDetails) return <Flex />;

    if (mode === MODE.EDIT) {
      return (
        <>
          <Flex direction="column" width="100%" height="100%" as="div">
            <Flex
              cursor="pointer"
              alignItems={'center'}
              onClick={() => {
                setMode(MODE.DEFAULT);
                history.goBack;
              }}
            >
              <ArrowBackIcon color="primary" />
              <Typography
                style={{ marginLeft: 8 }}
                variant="type8"
                color="primary"
                weight="600"
              >
                Go Back
              </Typography>
            </Flex>
            <UserInfoEdit
              refetch={refetch}
              onBack={() => setMode(MODE.DEFAULT)}
              data={fullDetails}
            />
          </Flex>
        </>
      );
    }

    return (
      <UserFullDetails
        refetch={handleRefetch}
        data={fullDetails}
        onSetMode={(m: MODE) => setMode(m)}
      />
    );
  }, [mode, fullDetails]);

  return (
    <AppModal
      {...props}
      size="5xl"
      onClose={() => {
        setMode(MODE.DEFAULT);
        props.onClose();
      }}
      HeaderRight={
        mode !== MODE.EDIT ? (
          <>
            <Flex
              position="absolute"
              top={2}
              right={14}
              zIndex={200}
              alignItems="center"
            >
              <Button
                variant="small"
                outline
                onClick={() => setNotesOpen(true)}
                RightComponent={
                  <Flex ml={1}>
                    <Caret color="primary" />
                  </Flex>
                }
              >
                Notes
              </Button>
              <Flex
                zIndex={200}
                ml={3}
                cursor="pointer"
                onClick={history.goBack}
              >
                <ArrowBackIcon />
              </Flex>
            </Flex>
            {id && notesOpen && (
              <Notes id={id} onClose={() => setNotesOpen(false)} />
            )}
          </>
        ) : null
      }
      onOverlayClick={props.onClose}
      title={
        fullDetails ? `${fullDetails?.firstName} ${fullDetails?.lastName}` : ''
      }
    >
      {!props.disableNext && (
        <Flex
          position="absolute"
          right="-150px"
          top="40vh"
          width="60px"
          height="60px"
          borderRadius="30px"
          zIndex="100"
          backgroundColor="#fff"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={props.onNext}
        >
          <ArrowRightIcon />
        </Flex>
      )}
      {!props.disablePrev && (
        <Flex
          position="absolute"
          left="-150px"
          top="40vh"
          width="60px"
          height="60px"
          borderRadius="30px"
          zIndex="100"
          backgroundColor="#fff"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={props.onPrev}
        >
          <ArrowLeftIcon />
        </Flex>
      )}
      {Content}
    </AppModal>
  );
};

export default UserDetailsView;
